'use client';

import { Button } from '@/app/components/ui/button';
import { Icon } from '@iconify/react';
import { AnimatePresence } from 'framer-motion';
import { Loader2 } from 'lucide-react'; // Импортируем спиннер
import dynamic from 'next/dynamic';
import Image from 'next/image';
import { useCallback, useState } from 'react';

// (1) Оптимизация: Динамический импорт с отключенным SSR и лоадером
const TomskGuideModal = dynamic(
  () => import('./TomskGuideModal').then((mod) => mod.TomskGuideModal),
  {
    loading: () => null, // Модалка гида легкая, лоадер не обязателен
  },
);

// 🔥 ВАЖНОЕ ИСПРАВЛЕНИЕ: ssr: false для Карты
const InteractiveMapModal = dynamic(
  () => import('./InteractiveMapModal').then((mod) => mod.InteractiveMapModal),
  {
    ssr: false, // Отключаем серверный рендеринг, так как карта требует window
    loading: () => (
      // Красивый лоадер, пока подгружается чанк с картой
      <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm">
        <Loader2 className="animate-spin text-white w-12 h-12" />
      </div>
    ),
  },
);

interface TourismHeroProps {
  title: string;
  description: string;
}

export function TourismHero({ title, description }: TourismHeroProps) {
  const [isGuideOpen, setIsGuideOpen] = useState(false);
  const [isMapOpen, setIsMapOpen] = useState(false);

  const openGuide = useCallback(() => setIsGuideOpen(true), []);
  const closeGuide = useCallback(() => setIsGuideOpen(false), []);

  const openMap = useCallback(() => setIsMapOpen(true), []);
  const closeMap = useCallback(() => setIsMapOpen(false), []);

  return (
    <>
      <section
        aria-labelledby="hero-heading"
        className="relative w-full h-[50vh] md:h-[60vh] rounded-xl overflow-hidden shadow-2xl"
      >
        <Image
          src="/PhotoTomsk/beautiful-tomsk-photo.webp"
          alt="Панорама города Томска на закате, вид на деревянную архитектуру и реку Томь"
          fill
          className="object-cover"
          priority
          sizes="(max-width: 768px) 100vw, (max-width: 1200px) 100vw, 100vw"
          // Добавьте sizes, чтобы Next.js мог выбирать правильный размер
        />

        <div
          role="toolbar"
          aria-label="Основные действия"
          className="absolute top-4 right-4 flex flex-col sm:flex-row gap-2 z-10"
        >
          <Button
            onClick={openGuide}
            aria-label="Открыть гид по городу Томск"
            className="bg-white/80 text-slate-800 hover:bg-white dark:bg-black/40 dark:text-white dark:hover:bg-black/60 backdrop-blur-sm border-none shadow-md"
          >
            <Icon icon="mdi:information-outline" className="mr-2" aria-hidden="true" />
            <span className="hidden sm:inline">Гид по городу</span>
            <span className="sm:hidden">Гид</span>
          </Button>
          <Button
            id="interactive-map"
            onClick={openMap}
            aria-label="Открыть интерактивную карту Томска"
            className="bg-white/80 text-slate-800 hover:bg-white dark:bg-black/40 dark:text-white dark:hover:bg-black/60 backdrop-blur-sm border-none shadow-md"
          >
            <Icon icon="mdi:map-marker-radius" className="mr-2" aria-hidden="true" />
            <span className="hidden sm:inline">Интерактивная карта</span>
            <span className="sm:hidden">Карта</span>
          </Button>
        </div>

        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />

        <div className="absolute inset-0 flex flex-col justify-center items-center p-6 text-center text-white">
          <h2
            id="hero-heading"
            className="text-2xl sm:text-4xl lg:text-6xl text-main/90 font-extrabold drop-shadow-lg"
          >
            {title}
          </h2>
          <p className="mt-4 max-w-3xl text-md sm:text-lg md:text-xl text-white drop-shadow-md">
            {description}
          </p>
        </div>
      </section>

      {/* Модалки рендерятся условно. 
          Благодаря dynamic import, код InteractiveMapModal загрузится по сети 
          ТОЛЬКО когда isMapOpen станет true.
      */}
      <AnimatePresence>
        {isGuideOpen && <TomskGuideModal onClose={closeGuide} />}
        {isMapOpen && <InteractiveMapModal onClose={closeMap} />}
      </AnimatePresence>
    </>
  );
}
